import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { CloseButton } from '../../../shared/ui';

import { makeMacCardUrl } from './MacCard';

export interface IMacCardViewerProps {
  cards: Array<{ id: number; img: string }>;
  initialIndex: number;
  onClose: () => void;
}

/** Полноэкранный просмотр карт: свайп влево/вправо между картами, тап по фону или ✕ — закрыть. */
export const MacCardViewer: FC<IMacCardViewerProps> = ({ cards, initialIndex, onClose }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(initialIndex);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) {
      el.scrollLeft = el.clientWidth * initialIndex;
    }
  }, [initialIndex]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-label="Просмотр карты"
      className="fixed inset-0 z-modal flex flex-col bg-black/90 pb-[env(safe-area-inset-bottom,0px)] pt-[env(safe-area-inset-top,0px)]"
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-2 text-white">
        <span className="text-ui text-white/80" aria-live="polite">
          {cards.length > 1 ? `${index + 1} / ${cards.length}` : ''}
        </span>
        <CloseButton tone="inverse" onClick={onClose} />
      </div>
      <div
        ref={scrollerRef}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overscroll-contain"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.clientWidth > 0) {
            setIndex(Math.round(el.scrollLeft / el.clientWidth));
          }
        }}
      >
        {cards.map((card) => (
          <div
            key={card.id}
            className="flex h-full w-full shrink-0 snap-center items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <img
              src={makeMacCardUrl(card.img)}
              alt={`Карта ${card.id}`}
              className="max-h-full max-w-full rounded-card object-contain"
            />
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
};
